"use client";

import React, { createContext, useContext, useState, useEffect } from "react";
import { addDays, format } from "date-fns";
import { useSearchParams } from "next/navigation";

export interface SelectedRoom {
  id: string; // Unique identifier: `${roomId}-${rateCode}`
  roomId: number;
  roomName: string;
  price: number;
  quantity: number;
  imageUrl?: string;
  guests: number;
  rateCode?: string;
  includeBreakfast?: boolean;
}

export interface SelectedAddon {
  code: string;
  name: string;
  price: number;
  quantity: number;
}

interface BookingContextType {
  // Search Params
  checkIn: Date;
  checkOut: Date;
  adults: number;
  children: number;
  roomCount: number; // Number of rooms requested in search
  
  // Hotel Config
  db: string;
  hotelId: number;

  // Selection
  selectedRooms: SelectedRoom[];
  selectedAddons: SelectedAddon[];

  // Methods
  setCheckIn: (date: Date) => void;
  setCheckOut: (date: Date) => void;
  setAdults: (n: number) => void;
  setChildren: (n: number) => void;
  setRoomCount: (n: number) => void;
  
  addRoom: (room: SelectedRoom) => void;
  removeRoom: (id: string) => void;
  updateRoomQty: (id: string, qty: number) => void;
  
  addAddon: (addon: SelectedAddon) => void;
  removeAddon: (code: string) => void;
  updateAddonQty: (code: string, qty: number) => void;
  
  clearCart: () => void;

  getTotalPrice: () => number;
}

const BookingContext = createContext<BookingContextType | undefined>(undefined);

export function BookingProvider({ children }: { children: React.ReactNode }) {
  const searchParams = useSearchParams();
  
  // Defaults
  const [checkIn, setCheckIn] = useState<Date>(() => {
    const param = searchParams?.get("checkIn");
    return param ? new Date(param) : addDays(new Date(), 1);
  });
  const [checkOut, setCheckOut] = useState<Date>(() => {
    const param = searchParams?.get("checkOut");
    return param ? new Date(param) : addDays(new Date(), 2);
  });
  const [adults, setAdults] = useState(() => {
    const param = searchParams?.get("adults");
    return param ? parseInt(param) : 2;
  });
  const [childrenCount, setChildren] = useState(() => {
    const param = searchParams?.get("children");
    return param ? parseInt(param) : 0;
  });
  const [roomCount, setRoomCount] = useState(() => {
    const param = searchParams?.get("roomCount");
    return param ? parseInt(param) : 1;
  });
  
  const [selectedRooms, setSelectedRooms] = useState<SelectedRoom[]>([]);
  const [selectedAddons, setSelectedAddons] = useState<SelectedAddon[]>([]);

  // Sync state with URL params (e.g. on back/forward navigation)
  useEffect(() => {
    if (!searchParams) return;

    const checkInParam = searchParams.get("checkIn");
    if (checkInParam) {
      const date = new Date(checkInParam);
      if (!isNaN(date.getTime()) && date.getTime() !== checkIn.getTime()) {
        setCheckIn(date);
      }
    }

    const checkOutParam = searchParams.get("checkOut");
    if (checkOutParam) {
      const date = new Date(checkOutParam);
      if (!isNaN(date.getTime()) && date.getTime() !== checkOut.getTime()) {
        setCheckOut(date);
      }
    }

    const adultsParam = searchParams.get("adults");
    if (adultsParam) {
      const val = parseInt(adultsParam);
      if (!isNaN(val) && val !== adults) {
        setAdults(val);
      }
    }

    const childrenParam = searchParams.get("children");
    if (childrenParam) {
      const val = parseInt(childrenParam);
      if (!isNaN(val) && val !== childrenCount) {
        setChildren(val);
      }
    }

    const roomCountParam = searchParams.get("roomCount");
    if (roomCountParam) {
      const val = parseInt(roomCountParam);
      if (!isNaN(val) && val !== roomCount) {
        setRoomCount(val);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // Get DB and Hotel ID from URL (e.g. ?db=PMH&hotel_id=1)
    // Default to DEMO and 1 if not present for demo purposes
    const db = searchParams?.get("db") || "DEMO"; 
    const hotelId = parseInt(searchParams?.get("hotel_id") || "1");

  const addRoom = (room: SelectedRoom) => {
    setSelectedRooms((prev) => {
      const existing = prev.find((r) => r.id === room.id);
      if (existing) {
        return prev.map((r) =>
          r.id === room.id
            ? { ...r, quantity: r.quantity + room.quantity }
            : r
        );
      }
      return [...prev, room];
    });
  };

  const removeRoom = (id: string) => {
    setSelectedRooms((prev) => prev.filter((r) => r.id !== id));
  };

  const updateRoomQty = (id: string, qty: number) => {
    if (qty <= 0) {
      removeRoom(id);
      return;
    }
    setSelectedRooms((prev) =>
      prev.map((r) => (r.id === id ? { ...r, quantity: qty } : r))
    );
  };

  const addAddon = (addon: SelectedAddon) => {
    setSelectedAddons((prev) => {
      const existing = prev.find((a) => a.code === addon.code);
      if (existing) {
        return prev.map((a) =>
          a.code === addon.code
            ? { ...a, quantity: a.quantity + addon.quantity }
            : a
        );
      }
      return [...prev, addon];
    });
  };

  const removeAddon = (code: string) => {
    setSelectedAddons((prev) => prev.filter((a) => a.code !== code));
  };

  const updateAddonQty = (code: string, qty: number) => {
    if (qty <= 0) {
      removeAddon(code);
      return;
    }
    setSelectedAddons((prev) =>
      prev.map((a) => (a.code === code ? { ...a, quantity: qty } : a))
    );
  };

  const clearCart = () => {
    setSelectedRooms([]);
    setSelectedAddons([]);
  };

  const getTotalPrice = () => {
    // Room price is already total for the stay (calculated in SearchPage)
    const roomsTotal = selectedRooms.reduce((acc, r) => acc + r.price * r.quantity, 0);
    const addonsTotal = selectedAddons.reduce((acc, a) => acc + a.price * a.quantity, 0);
    
    return roomsTotal + addonsTotal;
  };

  // Clear selections if dates change to avoid price mismatch
  useEffect(() => {
    setSelectedRooms([]);
    setSelectedAddons([]);
  }, [checkIn, checkOut]);

  return (
    <BookingContext.Provider
      value={{
        checkIn,
        setCheckIn,
        checkOut,
        setCheckOut,
        adults,
        setAdults,
        children: childrenCount,
        setChildren: setChildren,
        roomCount,
        setRoomCount,
        db,
        hotelId,
        selectedRooms,
        selectedAddons,
        addRoom,
        removeRoom,
        updateRoomQty,
        addAddon,
        removeAddon,
        updateAddonQty,
        clearCart,
        getTotalPrice,
      }}
    >
      {children}
    </BookingContext.Provider>
  );
}

export function useBooking() {
  const context = useContext(BookingContext);
  if (context === undefined) {
    throw new Error("useBooking must be used within a BookingProvider");
  }
  return context;
}
